import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { masterApis } from "stores/master/masterApis";

const useStyles = makeStyles({
  container: {
    width: "100%",
    boxSizing: "border-box",
    background: "#fff",
    color: "#111827",
    overflowX: "hidden",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    padding: "10px 0",
    border: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "14px",
    cursor: "pointer",
    position: "relative",
    zIndex: 2,
    "@media (max-width: 899.95px)": {
      padding: "6px 0",
      fontSize: "12px",
      // Sticky under navbar — light edge instead of floating shadow
      boxShadow: "none",
    },
  },

  ticker: {
    display: "inline-block",
    paddingLeft: "100%",
    animation: "$scroll 4500s linear infinite",

    "&:hover": {
      animationPlayState: "paused",
    },
  },

  "@keyframes scroll": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-50%)" },
  },

  item: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginRight: 48,
    "@media (max-width: 899.95px)": {
      gap: 6,
      marginRight: 32,
    },
  },

  symbol: {
    fontWeight: 700,
    color: "#111827",
    letterSpacing: "0.01em",
  },

  price: {
    color: "#374151",
    fontWeight: 500,
  },

  change: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 600,
  },

  up: {
    color: "#16A34A",
  },

  down: {
    color: "#DC2626",
  },

  neutral: {
    color: "#6B7280",
  },
});

const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "0%";
  }

  const numericValue = Number(value);
  const sign = numericValue > 0 ? "+" : "";
  return `${sign}${numericValue}%`;
};

const StockTicker = () => {
  const classes = useStyles();
  const [stocks, setStocks] = useState([]);

  const loadData = async () => {
    try {
      const res = await masterApis.getStockTicker();
      const filterData = res.filter((item) => item.returnsPercent !== null);
      setStocks(filterData || []);
    } catch (err) {
      console.error("Ticker error:", err);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 300000);
    return () => clearInterval(interval);
  }, []);

  const renderItem = (stock, index) => {
    const isUp = stock.returnsPercent > 0;
    const isDown = stock.returnsPercent < 0;
    const changeClass = isUp ? classes.up : isDown ? classes.down : classes.neutral;
    const arrow = isUp ? "▲" : isDown ? "▼" : "–";

    return (
      <span className={classes.item} key={`${stock.symbol}-${index}`}>
        <span className={classes.symbol}>{stock.symbol}</span>
        {stock.stockLatestPrice ? (
          <span className={classes.price}>₹{stock.stockLatestPrice}</span>
        ) : null}
        <span className={`${classes.change} ${changeClass}`}>
          <span>{arrow}</span>
          <span>{formatPercent(stock.returnsPercent)}</span>
        </span>
      </span>
    );
  };

  const tickerItems = stocks.length ? [...stocks, ...stocks] : [];

  return (
    <div className={classes.container}>
      <div className={classes.ticker}>
        {tickerItems.length === 0 ? (
          <>Loading stocks…</>
        ) : (
          tickerItems.map(renderItem)
        )}
      </div>
    </div>
  );
};

export default React.memo(StockTicker);
